import { getContext, setContext } from 'svelte';
import { errorReporting } from '@shared/error-reporting/ErrorReporting.browser.ts';
import type { ErrorReporting } from '@shared/error-reporting/ErrorReporting.ts';
import { createTileListOnEx } from '@tilelist/application/core/OnExFactory.ts';
import type { TileListInitialSession } from '@tilelist/application/core/TileListInitialSession.ts';
import type { TileListPageLoadResult } from '@tilelist/application/core/TileListSession.ts';
import type { EventsOutgoing } from '@tilelist/application/ports/EventsOutgoing.ts';
import type { TileListLoadedPayload } from '@tilelist/application/ports/TileListLoaded.ts';
import type {
    StrategyFactory,
    TileListBrowserWiring,
} from '@tilelist/application/wiring/TileListBrowserWiring.browser.ts';
import { createTileListBrowserWiring } from '@tilelist/application/wiring/TileListBrowserWiring.browser.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx.ts';

export type TileListWiring = {
    errorReporting: ErrorReporting;
    onex: OnEx;
    browser: TileListBrowserWiring | null;
};

export type MountedTileListWiring = {
    browserWiring: TileListBrowserWiring;
    cleanup: () => void;
};

type ReloadProducer = {
    session: Pick<TileListBrowserWiring['session'], 'reload'>;
    eventsOutgoing: Pick<EventsOutgoing, 'publishTileListLoaded'>;
};

export async function reloadAndEmitTileListLoaded(
    event: Parameters<TileListBrowserWiring['session']['reload']>[0],
    browserWiring: ReloadProducer,
    onReload: (result: TileListPageLoadResult) => void | Promise<void>,
): Promise<void> {
    const result = await browserWiring.session.reload(event);
    await onReload(result);
    if (!result.page) return;

    const payload: TileListLoadedPayload = {
        event,
        page: result.page,
        historic: result.historic,
        shouldScroll: result.shouldScroll,
    };
    browserWiring.eventsOutgoing.publishTileListLoaded(payload);
}

export const WIRING_KEY: unique symbol = Symbol('TileListWiring');

/**
 *
 *
 *
 *
 *
 */
export function createAndProvideWiring(initialSession: TileListInitialSession): TileListWiring {
    const onex = createTileListOnEx(initialSession.session);
    const wiring = $state<TileListWiring>({
        errorReporting,
        onex,
        browser: null,
    });
    setContext(WIRING_KEY, wiring);
    return wiring;
}

/**
 *
 *
 *
 *
 *
 */
export function mountWiring(
    ctx: TileListWiring,
    initialSession: TileListInitialSession,
    onReload: (result: TileListPageLoadResult, browserWiring: TileListBrowserWiring) => void | Promise<void>,
    strategyFactory?: StrategyFactory,
): MountedTileListWiring {
    const browserWiring = createTileListBrowserWiring(initialSession, strategyFactory);

    ctx.browser = browserWiring;

    const unsub = browserWiring.eventsIncoming.onPageResolved(async (event) => {
        await reloadAndEmitTileListLoaded(event, browserWiring, (result) => onReload(result, browserWiring));
    });

    return {
        browserWiring,
        cleanup: () => {
            unsub();
            browserWiring.cleanup();
        },
    };
}

const NULL_WIRING: TileListWiring = {
    errorReporting,
    /*                                                                             */
    /*                                                                              */
    get onex(): OnEx {
        throw new Error('injectWiring: no wiring context available (onex is unset)');
    },
    browser: null,
};

/**
 *
 *
 *
 *
 *
 *
 */
export function injectWiring(): TileListWiring {
    /*                                                                                                          */
    return getContext<TileListWiring>(WIRING_KEY) ?? NULL_WIRING;
}
